import { createClient } from "@supabase/supabase-js";
import { UserRole, getProfileTableFromUserRole } from "./auth-utils";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type UploadFileResult =
  | { success: true; path: string; name: string }
  | {
      success: false;
      error: "INVALID_TYPE" | "FILE_TOO_LARGE" | "UPLOAD_FAILED";
    };

type UploadAvatarResult =
  | { success: true; path: string }
  | {
      success: false;
      error: "INVALID_TYPE" | "FILE_TOO_LARGE" | "UPLOAD_FAILED";
    };

export type UploadMockInterviewResult =
  | { success: true; path: string }
  | {
      success: false;
      error: "INVALID_TYPE" | "FILE_TOO_LARGE" | "UPLOAD_FAILED";
    };

export type DeleteFileResult =
  | { success: true }
  | {
      success: false;
      error: "DELETE_FAILED";
    };

export interface TalentProfile {
  id: string;
  given_name: string;
  family_name: string;
  email: string;
  phone: string;
  nationality: string;
  additional_languages: Array<{
    language: string;
    level: string;
  }>;
  theory_hours_deficit: number | null;
  practice_hours_deficit: number | null;
  residence_permit_type: string;
  nursing_education_status: "completed" | "in_progress" | "not_started";
  estimated_completion_date: string | null;
  education_location: string;
  education_address: string;
  education_street: string;
  education_street_number: string;
  education_postal_code: string;
  education_city: string;
  education_state: string;
  education_country: string;
  profile_picture: string;
  [key: string]: any;
}

export interface ApplicationData {
  id: string;
  talent_id: string;
  job_id: string;
  status: "in_progress" | "paused" | "hired" | "declined";
  created_at: string;
  updated_at: string;
  internal_notes: string;
  [key: string]: any;
}

export interface CompanyData {
  id: string;
  name: string;
  website: string;
  calendly_link: string;
  created_at: string;
  industry: string;
  description: string;
}

export interface JobData {
  id: string;
  title: string;
  description: string;
  company_id: string;
  company_description?: string;
  [key: string]: any;
}

export type ApplicationsStatuses =
  | "screening"
  | "invited_to_interview"
  | "passed_interview"
  | "failed_interview"
  | "paused"
  | "hired"
  | "declined";

export interface ChatThread {
  id: number;
  talent_application: number;
  last_message_at: string;
  last_time_opened: string;
}

export interface ChatMessage {
  id: number;
  chat_thread: number;
  sender_id: string;
  message_text: string;
  sent_at: string;
}

export interface CookiePreferences {
  consent_status: "accepted" | "rejected" | "managed";
  version: string;
  timestamp: string;
  categories: {
    strictly_necessary: boolean;
    analytics: boolean;
    marketing: boolean;
  };
}

export enum TaskStatus {
  OPEN = "open",
  IN_PROGRESS = "in_progress",
  DONE = "done"
}

export interface TaskV2 {
  id: string;
  talent_profile?: string;
  application?: string;
  created_at: string;
  title: string;
  description: string;
  priority: number;
  status: TaskStatus;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper function to select table elements by id, it is expected that the table has an id column.
const selectTableElementsById = async (
  table: string,
  selectFields: string,
  id: string
): Promise<any> => {
  const { data, error } = await supabase
    .from(table)
    .select(selectFields)
    .eq("id", id)
    .single();
  if (error) throw error;
  if (!data) throw new Error(`No data found for ${table} with id ${id}`);

  return data;
};

// ============================================================================
// STORAGE & BUCKETS
// ============================================================================

async function uploadFile(
  file: File,
  bucket: string,
  path: string
): Promise<UploadFileResult> {
  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ];
  if (!allowedTypes.includes(file.type)) {
    return {
      success: false,
      error: "INVALID_TYPE"
    };
  }
  // Max file size (e.g., 5 MB)
  const maxSizeMB = 5;
  if (file.size > maxSizeMB * 1024 * 1024) {
    return {
      success: false,
      error: "FILE_TOO_LARGE"
    };
  }
  const fileExt = file.name.split(".").pop();
  const fileName = `${crypto.randomUUID()}.${fileExt}`;
  const filePath = `${path}/${fileName}`;
  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(filePath, file);
  if (uploadError) {
    throw uploadError;
  }

  return { path: filePath, name: fileName, success: true };
}

export async function uploadTalentProfileFile(
  file: File,
  path: string
): Promise<UploadFileResult> {
  try {
    const result = await uploadFile(file, "profiles", path);
    if (!result.success) return result;
    return result;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}

export async function uploadApplicationFile(
  file: File,
  path: string
): Promise<UploadFileResult> {
  try {
    const result = await uploadFile(file, "applications", path);
    if (!result.success) return result;
    return result;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}

export async function getSignedUrl(
  bucket: string,
  filePath: string,
  expiresIn: number = 3600 // 1 hour by default
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error("Signed URL error:", error);
    throw error;
  }
  return data.signedUrl;
}

export async function uploadAvatar(
  file: File,
  userId: string
): Promise<UploadAvatarResult> {
  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    return {
      success: false,
      error: "INVALID_TYPE"
    };
  }

  // Max file size (e.g., 2 MB)
  const maxSizeMB = 2;
  if (file.size > maxSizeMB * 1024 * 1024) {
    return {
      success: false,
      error: "FILE_TOO_LARGE"
    };
  }

  const fileExt = file.name.split(".").pop();
  const fileName = `${crypto.randomUUID()}.${fileExt}`;
  const filePath = `${userId}/avatar/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from("profiles")
    .upload(filePath, file);

  if (uploadError) {
    console.error("Upload error:", uploadError);
    return {
      success: false,
      error: "UPLOAD_FAILED"
    };
  }

  return { success: true, path: filePath };
}

export async function uploadMockInterview(
  file: File,
  userId: string
): Promise<UploadMockInterviewResult> {
  const allowedTypes = [
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/quicktime"
  ];
  if (!allowedTypes.includes(file.type)) {
    return {
      success: false,
      error: "INVALID_TYPE"
    };
  }

  // Max file size (e.g., 200 MB)
  const maxSizeMB = 200;
  if (file.size > maxSizeMB * 1024 * 1024) {
    return {
      success: false,
      error: "FILE_TOO_LARGE"
    };
  }

  const fileExt = file.name.split(".").pop();
  const fileName = `${crypto.randomUUID()}.${fileExt}`;
  const filePath = `${userId}/mock_interview/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from("profiles")
    .upload(filePath, file);

  if (uploadError) {
    console.error("Upload mock interview video error:", uploadError);
    return {
      success: false,
      error: "UPLOAD_FAILED"
    };
  }

  // Update the talent profile
  const { error: updateError } = await supabase
    .from("talent_profiles")
    .update({ mock_interview_video: filePath })
    .eq("id", userId);

  if (updateError) {
    console.error(
      "Profile update error with mock interview video:",
      updateError
    );
    return {
      success: false,
      error: "UPLOAD_FAILED"
    };
  }

  return { success: true, path: filePath };
}

export async function deleteMockInterview(
  userId: string,
  filePath: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const deleteResult = await deleteFile("profiles", filePath);
    if (!deleteResult.success) {
      return { success: false, error: "STORAGE_DELETE_FAILED" };
    }

    const { error: updateError } = await supabase
      .from("talent_profiles")
      .update({ mock_interview_video: null })
      .eq("id", userId);

    if (updateError) {
      console.error("Database update error:", updateError);
      return { success: false, error: "DATABASE_UPDATE_FAILED" };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in deleteMockInterview:", error);
    return { success: false, error: "DELETE_FAILED" };
  }
}

export async function deleteFile(
  bucket: "profiles" | "applications",
  filePath: string
): Promise<DeleteFileResult> {
  const { error } = await supabase.storage.from(bucket).remove([filePath]);
  if (error) {
    console.error("Delete error:", error);
    return {
      success: false,
      error: "DELETE_FAILED"
    };
  }
  return { success: true };
}

/**
 * Gets signed URL for profile picture
 */
export async function getProfilePictureUrl(
  profilePicture: string
): Promise<string | null> {
  try {
    return await getSignedUrl("profiles", profilePicture);
  } catch (error) {
    console.error("Error loading avatar:", error);
    return null;
  }
}

/**
 * Gets signed URL for mock interview video
 */
export async function getMockInterviewVideoUrl(
  mockInterviewVideo: string
): Promise<string | null> {
  try {
    return await getSignedUrl("profiles", mockInterviewVideo);
  } catch (error) {
    console.error("Error loading mock interview video:", error);
    return null;
  }
}

// ============================================================================
// TALENT PROFILES
// ============================================================================

// Abstracted method to get talent profiles based on user role
export async function getTalentsApplicationsByUserRole(
  userId: string,
  userRole: UserRole
): Promise<any[]> {
  const processTalentData = (data: any[]) => {
    return data.map((item) => ({ ...item, talent_id: item.id }));
  };
  try {
    // For superuser, return all talent profiles without filtering
    if (userRole === "superuser") {
      const { data, error } = await supabase
        .from("talent_profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return processTalentData(data || []);
    }

    // For partner role, get talent profiles where partner_id matches
    if (userRole === "partner") {
      const partnerProfile = await selectTableElementsById(
        "partner_profiles",
        "partner_id",
        userId
      );

      const { data, error } = await supabase
        .from("talent_profiles")
        .select("*")
        .eq("partner_id", partnerProfile.partner_id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return processTalentData(data || []);
    }

    // For admin/staff role, get talent profiles of talents that have applied to any job of the admin's company
    if (userRole === "admin" || userRole === "staff") {
      const employerProfile = await selectTableElementsById(
        "employer_profiles",
        "company_id",
        userId
      );

      // First get all applications for the company's jobs
      const { data: applications, error: applicationsError } = await supabase
        .from("talent_applications")
        .select("talent_id")
        .eq("company_id", employerProfile.company_id);

      if (applicationsError) throw applicationsError;

      if (!applications || applications.length === 0) {
        return [];
      }

      // Extract unique talent IDs
      const talentIds = [...new Set(applications.map((app) => app.talent_id))];

      // Get talent profiles for these talent IDs
      const { data, error } = await supabase
        .from("talent_profiles")
        .select("*")
        .in("id", talentIds)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return processTalentData(data || []);
    }
    // For talent role, return empty array (talents don't see other talent profiles)
    return [];
  } catch (error) {
    console.error("Error fetching talent profiles:", error);
    throw error;
  }
}

/**
 * Fetches talent profile data by ID
 */
export async function fetchTalentProfile(
  talentId: string
): Promise<TalentProfile> {
  const { data: profile, error } = await supabase
    .from("talent_profiles")
    .select("*")
    .eq("id", talentId)
    .single();

  if (error) throw error;

  return {
    ...profile,
    talent_id: profile.id,
    additional_languages: profile.additional_languages || []
  };
}

/**
 * Fetches talent profile associated with an application
 */
export async function fetchTalentProfileByApplication(
  talentId: string
): Promise<TalentProfile> {
  const { data: profile, error } = await supabase
    .from("talent_profiles")
    .select("*")
    .eq("id", talentId)
    .single();

  if (error) throw error;

  return {
    ...profile,
    talent_id: profile.id,
    additional_languages: profile.additional_languages || []
  };
}

export async function deleteTalentProfile(id: string) {
  const {
    data: { session },
    error: sessionError
  } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    throw new Error("User not logged in or session expired");
  }

  const response = await fetch(
    "https://vfpigaclbrmfhdvdtpyd.supabase.co/functions/v1/delete_user",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ id })
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to delete user");
  }
}

export async function addNewTalentProfile(
  email: string,
  password: string,
  givenName: string,
  familyName: string,
  partnerId?: string
) {
  const {
    data: { session },
    error: sessionError
  } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    throw new Error("User not logged in or session expired");
  }

  const response = await fetch(
    "https://vfpigaclbrmfhdvdtpyd.supabase.co/functions/v1/create_new_user",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        email,
        password,
        givenName,
        familyName,
        partnerId
      })
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to create user");
  }

  return data.user;
}

// ============================================================================
// APPLICATIONS
// ============================================================================

// Abstracted method to get applications based on user role
export async function getApplicationsByUserRole(
  userId: string,
  userRole: UserRole
): Promise<any[]> {
  // Helper function to transform raw data into expanded format, it also gives specific tables a specific id name to avoid conflicts.
  const transformApplicationData = (data: any[]) => {
    return data.map((item) => ({
      ...item.talent_profiles,
      talent_id: item.talent_profiles.id,
      job_title: item.jobs?.title || null,
      talent_application: item.id,
      ...item
    }));
  };

  // Helper function to fetch applications with common query structure
  const fetchApplications = async (additionalFilter?: {
    column: string;
    value: any;
  }) => {
    let query = supabase
      .from("talent_applications")
      .select("*, talent_profiles!inner(*), jobs(title)")
      .order("created_at", { ascending: false });
    if (additionalFilter) {
      query = query.eq(additionalFilter.column, additionalFilter.value);
    }

    const { data, error } = await query;
    if (error) throw error;

    return transformApplicationData(data || []);
  };

  try {
    // For superuser, return all applications without filtering
    if (userRole === "superuser") {
      return await fetchApplications();
    }

    // For partner role, get applications where talent belongs to that partner
    if (userRole === "partner") {
      const partnerProfile = await selectTableElementsById(
        "partner_profiles",
        "partner_id",
        userId
      );
      return await fetchApplications({
        column: "talent_profiles.partner_id",
        value: partnerProfile.partner_id
      });
    }

    // For admin/staff role, get applications for their company
    if (userRole === "admin" || userRole === "staff") {
      const employerProfile = await selectTableElementsById(
        "employer_profiles",
        "company_id",
        userId
      );

      return await fetchApplications({
        column: "company_id",
        value: employerProfile.company_id
      });
    }

    // For talent role, return empty array (talents don't see applications)
    return [];
  } catch (error) {
    console.error("Error fetching applications:", error);
    throw error;
  }
}

/**
 * Fetches application data by ID
 */
export async function fetchApplicationData(
  applicationId: string
): Promise<ApplicationData> {
  const { data: application, error } = await supabase
    .from("talent_applications")
    .select("*")
    .eq("id", applicationId)
    .single();

  if (error) throw error;
  return application;
}

/**
 * Updates application status in the database
 */
export async function updateApplicationStatus(
  applicationId: string,
  status: ApplicationsStatuses
): Promise<void> {
  const { error } = await supabase
    .from("talent_applications")
    .update({ status })
    .eq("id", applicationId);

  if (error) throw error;
}

export async function updateApplicationInterviewStatus(
  applicationId: string,
  interviewStatus: string
): Promise<void> {
  const { error } = await supabase
    .from("talent_applications")
    .update({ interview_status: interviewStatus })
    .eq("id", applicationId);
  if (error) throw error;
}

export async function updateApplicationInterviewDate(
  applicationId: string,
  interviewDate: string | null
): Promise<void> {
  const { error } = await supabase
    .from("talent_applications")
    .update({ interview_date: interviewDate })
    .eq("id", applicationId);
  if (error) throw error;
}

export async function updateApplicationInterviewLink(
  applicationId: string,
  interviewLink: string | null
): Promise<void> {
  const { error } = await supabase
    .from("talent_applications")
    .update({ interview_link: interviewLink })
    .eq("id", applicationId);
  if (error) throw error;
}

export async function cancelApplicationInterviewStatus(
  applicationId: string
): Promise<void> {
  const { error } = await supabase
    .from("talent_applications")
    .update({ interview_status: null, interview_date: null })
    .eq("id", applicationId);
  if (error) throw error;
}

/**
 * Updates application internal notes
 */
export async function updateApplicationNotes(
  applicationId: string,
  notes: string
): Promise<void> {
  const { error } = await supabase
    .from("talent_applications")
    .update({ internal_notes: notes })
    .eq("id", applicationId);

  if (error) throw error;
}

// ============================================================================
// JOBS & COMPANIES
// ============================================================================

/**
 * Fetches job data by ID
 */
export async function fetchJobData(jobId: string): Promise<JobData | null> {
  try {
    const { data: jobData, error: jobError } = await supabase
      .from("jobs")
      .select("*")
      .eq("id", jobId)
      .single();

    if (jobError) throw jobError;

    // Fetch company description if available
    if (jobData.company_id) {
      const companyData = await fetchCompanyData(jobData.company_id);
      if (companyData?.description) {
        return {
          ...jobData,
          company_description: companyData.description
        };
      }
    }

    return jobData;
  } catch (error) {
    console.error("Error fetching job:", error);
    return null;
  }
}

/**
 * Fetches company data by ID
 */
export async function fetchCompanyData(
  companyId: string
): Promise<CompanyData | null> {
  try {
    const { data: companyData, error } = await supabase
      .from("companies")
      .select("*")
      .eq("id", companyId)
      .single();

    if (error) throw error;
    return companyData;
  } catch (error) {
    console.error("Error fetching company:", error);
    return null;
  }
}

// ============================================================================
// TASKS V2
// ============================================================================

/**
 * Fetches tasks associated with a specific talent profile
 */
export async function getTalentTasksV2(
  talentProfileId: string
): Promise<TaskV2[]> {
  const { data, error } = await supabase
    .from("tasks_v2")
    .select("*")
    .eq("talent_profile", talentProfileId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Fetches tasks associated with a specific application
 */
export async function getApplicationTasksV2(
  applicationId: string
): Promise<TaskV2[]> {
  const { data, error } = await supabase
    .from("tasks_v2")
    .select("*")
    .eq("application", applicationId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Fetches a single task by ID
 */
export async function getTaskV2ById(id: string): Promise<TaskV2> {
  const { data, error } = await supabase
    .from("tasks_v2")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Adds a new task to tasks_v2
 */
export async function addTaskV2(
  task: Omit<TaskV2, "id" | "created_at">
): Promise<TaskV2> {
  const { data, error } = await supabase
    .from("tasks_v2")
    .insert(task)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates an existing task
 */
export async function updateTaskV2(
  id: string,
  updates: Partial<
    Pick<TaskV2, "title" | "description" | "status" | "priority">
  >
): Promise<TaskV2> {
  const { data, error } = await supabase
    .from("tasks_v2")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes a task from tasks_v2
 */
export async function deleteTaskV2(id: string): Promise<void> {
  const { error } = await supabase.from("tasks_v2").delete().eq("id", id);

  if (error) throw error;
}

// ============================================================================
// CHAT
// ============================================================================

/**
 * Retrieves the most recent messages for a given application ID.
 *
 * @param applicationId The ID of the application.
 * @param limit The maximum number of messages to return.
 * @param offset The number of messages to skip (for pagination).
 * @returns An ordered array of message objects.
 */
export async function ReadLastNMessagesForApplication(
  applicationId: string,
  limit: number = 50,
  offset: number = 0
): Promise<ChatMessage[]> {
  try {
    // 1. Find the chat_thread associated with the input applicationId.
    const { data: threadData, error: threadError } = await supabase
      .from("chat_thread")
      .select("id")
      .eq("talent_application", applicationId)
      .single();

    if (threadError) {
      // If no thread exists, it's not necessarily an error, just means no messages yet.
      // However, if it's a real DB error, we should probably throw or log.
      // For "PGRST116" (JSON object requested, multiple (or no) rows returned), if it's "no rows", return empty.
      if (threadError.code === "PGRST116") {
        return [];
      }
      throw threadError;
    }

    if (!threadData) {
      return [];
    }

    // 2. Select messages from chat_message where chat_thread matches.
    // 3. Order the results by sent_at descending.
    // 4. Apply limit and offset.
    const { data: messages, error: messagesError } = await supabase
      .from("chat_message")
      .select("*")
      .eq("chat_thread", threadData.id)
      .order("sent_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (messagesError) throw messagesError;

    return messages || [];
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
}

/**
 * Inserts a new message into the correct thread, and simultaneously updates the thread's metadata.
 * Note: This runs as a sequence of operations on the client side.
 *
 * @param applicationId The ID of the application.
 * @param senderId The ID of the sender.
 * @param messageText The text of the message.
 * @returns The newly created message object.
 */
export async function AddApplicationMessage(
  applicationId: string,
  senderId: string,
  messageText: string
): Promise<ChatMessage> {
  try {
    // 1. Get or Create Thread
    // We use upsert on the chat_thread table to ensure a thread exists and update the timestamp.
    // Assuming talent_application is a unique constraint or primary key allows upsert to work as "insert or update".
    // We want to update `last_message_at` to now.
    const now = new Date().toISOString();

    const { data: threadData, error: threadError } = await supabase
      .from("chat_thread")
      .upsert(
        {
          talent_application: applicationId,
          last_message_at: now
        },
        { onConflict: "talent_application" }
      )
      .select()
      .single();
    if (threadError) throw threadError;
    if (!threadData) throw new Error("Failed to get or create chat thread.");

    // 2. Insert the new message
    const { data: messageData, error: messageError } = await supabase
      .from("chat_message")
      .insert({
        chat_thread: threadData.id,
        sender_id: senderId,
        message_text: messageText,
        sent_at: now
      })
      .select()
      .single();

    if (messageError) throw messageError;
    if (!messageData) throw new Error("Failed to insert message.");

    return messageData;
  } catch (error) {
    console.error("Error adding message:", error);
    throw error;
  }
}

/**
 * Retrieves the chat thread details for a given application ID.
 *
 * @param applicationId The ID of the application.
 * @returns The chat thread object or null if not found.
 */
export async function getChatThread(
  applicationId: string
): Promise<ChatThread | null> {
  try {
    const { data, error } = await supabase
      .from("chat_thread")
      .select("*")
      .eq("talent_application", applicationId)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null; // No rows found
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error fetching chat thread:", error);
    return null;
  }
}

/**
 * Marks the chat thread as read by updating the last_time_opened timestamp.
 *
 * @param threadId The ID of the chat thread.
 */
export async function markThreadAsRead(threadId: number): Promise<void> {
  try {
    const { error } = await supabase
      .from("chat_thread")
      .update({ last_time_opened: new Date().toISOString() })
      .eq("id", threadId);

    if (error) throw error;
  } catch (error) {
    console.error("Error marking thread as read:", error);
    throw error;
  }
}

// ============================================================================
// PARTNERS
// ============================================================================

export async function getPartners(): Promise<{ id: string; name: string }[]> {
  const { data, error } = await supabase
    .from("partners")
    .select("id, name")
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching partners:", error);
    return [];
  }

  return data || [];
}

// ============================================================================
// USER PREFERENCES (COOKIES)
// ============================================================================

export async function getUserPreferences(
  userId: string,
  role: UserRole
): Promise<{ preferences: any } | null> {
  const table = getProfileTableFromUserRole(role);
  if (!table) return null;
  try {
    const { data, error } = await supabase
      .from(table)
      .select("preferences")
      .eq("id", userId)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    console.error("Error fetching user preferences:", error);
    return null;
  }
}

export async function updateUserCookiePreferences(
  userId: string,
  role: UserRole,
  cookiePreferences: CookiePreferences
): Promise<void> {
  const table = getProfileTableFromUserRole(role);
  if (!table) return;

  try {
    // 1. Fetch existing preferences
    const existingData = await getUserPreferences(userId, role);
    const existingPreferences = existingData?.preferences || {};

    // 2. Merge with new cookie preferences
    const updatedPreferences = {
      ...existingPreferences,
      cookies: cookiePreferences
    };
    // 3. Update the database
    const { error } = await supabase
      .from(table)
      .update({ preferences: updatedPreferences })
      .eq("id", userId);

    if (error) throw error;
  } catch (error) {
    console.error("Error updating user cookie preferences:", error);
    throw error;
  }
}

export async function getAllTalents(): Promise<
  { id: string; given_name: string; family_name: string; email: string }[]
> {
  const { data, error } = await supabase
    .from("talent_profiles")
    .select("id, given_name, family_name, email")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching all talents:", error);
    return [];
  }

  return data || [];
}
